import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  createRoleRequest,
  deleteRoleRequest,
  fetchRolesList,
  getAdminIdFromToken,
  updateRoleRequest,
} from "../api/userBuilderApi";
import { isSystemRoleRow } from "../../../public/utils/roleAccess";

const RoleSectionContext = createContext(null);

const PAGE_SIZE = 10;

function emptyRoleForm() {
  return { role_name: "", description: "" };
}

function validateRoleForm(form) {
  const role_name = (form.role_name ?? "").trim();
  const description = (form.description ?? "").trim();
  const fieldErrors = { role_name: "", description: "" };

  if (!role_name) fieldErrors.role_name = "Role name is required";
  else if (role_name.length < 2 || role_name.length > 100) {
    fieldErrors.role_name = "Role name must be 2–100 characters";
  } else if (!/^[a-zA-Z0-9][a-zA-Z0-9\s\-_.]*$/.test(role_name)) {
    fieldErrors.role_name = "Use letters, numbers, spaces, or hyphens only";
  }

  const ok = !Object.values(fieldErrors).some(Boolean);
  return { ok, fieldErrors, role_name, description: description || null };
}

export function RoleSectionProvider({
  children,
  onRolesChanged,
  onNotify,
  onEngageSection,
}) {
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [form, setForm] = useState(emptyRoleForm);
  const [busy, setBusy] = useState(false);
  const [rolePendingRemoval, setRolePendingRemoval] = useState(null);
  const [removingRole, setRemovingRole] = useState(false);

  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [listLoading, setListLoading] = useState(true);
  const [directoryError, setDirectoryError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({ role_name: "", description: "" });
  const [submitError, setSubmitError] = useState("");
  const formAlertRef = useRef(null);

  const hasFieldErrors = Object.values(fieldErrors).some(Boolean);
  const showFormAlert = !!(submitError || hasFieldErrors);

  useEffect(() => {
    if (!showFormAlert) return;
    formAlertRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [showFormAlert, submitError, hasFieldErrors]);

  const loadPage = useCallback(
    async (p) => {
      setListLoading(true);
      setDirectoryError("");
      try {
        const r = await fetchRolesList(p, PAGE_SIZE);
        setRows(r.rows);
        setTotal(r.total);
        setTotalPages(r.totalPages);
        setPage(r.page);
      } catch (e) {
        setRows([]);
        const msg = e.message || "Could not load roles.";
        setDirectoryError(msg);
        onNotify?.(msg, { variant: "error" });
      } finally {
        setListLoading(false);
      }
    },
    [onNotify],
  );

  useEffect(() => {
    loadPage(page);
  }, [page, loadPage]);

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => (prev[name] ? { ...prev, [name]: "" } : prev));
    setSubmitError("");
  };

  const resetForm = () => {
    setEditingRoleId(null);
    setForm(emptyRoleForm());
    setFieldErrors({ role_name: "", description: "" });
    setSubmitError("");
  };

  const startEdit = (row) => {
    if (isSystemRoleRow(row)) return;
    setEditingRoleId(row.id);
    setForm({
      role_name: row.role_name || "",
      description: row.description || "",
    });
    setFieldErrors({ role_name: "", description: "" });
    setSubmitError("");
    onEngageSection?.();
    window.setTimeout(() => {
      document.getElementById("role-name")?.focus({ preventScroll: true });
    }, 80);
  };

  const submit = async () => {
    setSubmitError("");
    setFieldErrors({ role_name: "", description: "" });

    const check = validateRoleForm(form);
    setFieldErrors(check.fieldErrors);
    if (!check.ok) return;

    const adminId = getAdminIdFromToken();
    setBusy(true);
    try {
      if (editingRoleId != null) {
        await updateRoleRequest(editingRoleId, {
          role_name: check.role_name,
          description: check.description,
        });
        onNotify?.("Role updated.");
      } else {
        const created = await createRoleRequest({
          role_name: check.role_name,
          description: check.description,
          created_by: adminId,
        });
        onNotify?.(
          created?.restored
            ? "Role restored."
            : created?.message || "Role created.",
        );
      }
      resetForm();
      await onRolesChanged?.();
      if (editingRoleId != null) {
        await loadPage(page);
      } else if (page === 1) {
        await loadPage(1);
      } else {
        setPage(1);
      }
    } catch (e) {
      setSubmitError(e.message || "Could not save role.");
    } finally {
      setBusy(false);
    }
  };

  const requestRoleRemoval = (row) => {
    if (isSystemRoleRow(row)) return;
    setRolePendingRemoval(row);
  };

  const cancelRoleRemoval = () => {
    if (removingRole) return;
    setRolePendingRemoval(null);
  };

  const confirmRoleRemoval = async () => {
    const row = rolePendingRemoval;
    if (!row || removingRole || isSystemRoleRow(row)) return;
    onNotify?.("");
    setRemovingRole(true);
    setBusy(true);
    try {
      await deleteRoleRequest(row.id);
      if (editingRoleId === row.id) resetForm();
      onNotify?.("Role removed from the directory.");
      await onRolesChanged?.();
      await loadPage(page);
      setRolePendingRemoval(null);
    } catch (e) {
      onNotify?.(e.message || "Could not remove role.", { variant: "error" });
    } finally {
      setBusy(false);
      setRemovingRole(false);
    }
  };

  const value = {
    form,
    setField,
    submit,
    resetForm,
    isEdit: editingRoleId != null,
    busy,
    showFormAlert,
    formAlertRef,
    fieldErrors,
    submitError,
    directoryError,
    listLoading,
    page,
    totalPages,
    total,
    setPage,
    rows,
    startEdit,
    rolePendingRemoval,
    removingRole,
    requestRoleRemoval,
    cancelRoleRemoval,
    confirmRoleRemoval,
    pageSize: PAGE_SIZE,
    isSystemRoleRow,
  };

  return (
    <RoleSectionContext.Provider value={value}>{children}</RoleSectionContext.Provider>
  );
}

export function useRoleSectionContext() {
  const ctx = useContext(RoleSectionContext);
  if (!ctx) {
    throw new Error("useRoleSectionContext must be used within RoleSectionProvider");
  }
  return ctx;
}
